import { useState } from 'react';

const methods = [
  {
    id: 'flat',
    name: 'Apuesta Plana',
    risk: 'Bajo',
    riskColor: '#00e87a',
    description: 'Apuestas siempre la misma cantidad fija, independientemente de tu confianza en la selección. Es el método más seguro y recomendado para principiantes.',
    pros: ['Máximo control del riesgo', 'Fácil de seguir y registrar', 'Protege ante rachas negativas', 'Ideal para empezar'],
    cons: ['Crecimiento más lento del bankroll', 'No aprovecha apuestas de alta confianza'],
    example: 'Bankroll: 1.000€ → Apuesta fija: 20€ (2%) por selección',
    icon: 'ri-subtract-line',
  },
  {
    id: 'kelly',
    name: 'Criterio de Kelly',
    risk: 'Medio',
    riskColor: '#f59e0b',
    description: 'Calcula el tamaño óptimo de la apuesta en función de tu ventaja percibida sobre la casa. Maximiza el crecimiento a largo plazo si tus estimaciones son correctas.',
    pros: ['Maximiza el crecimiento del bankroll', 'Matemáticamente óptimo', 'Adapta el tamaño al valor real'],
    cons: ['Requiere estimaciones precisas de probabilidad', 'Puede generar apuestas muy grandes', 'Riesgo alto si sobreestimas el valor'],
    example: 'f = (bp - q) / b → donde b=cuota-1, p=prob. estimada, q=1-p',
    icon: 'ri-calculator-line',
  },
  {
    id: 'fractional',
    name: 'Kelly Fraccionado',
    risk: 'Medio-Bajo',
    riskColor: '#06b6d4',
    description: 'Versión conservadora del Kelly: aplicas solo el 25-50% de lo que Kelly recomienda. Reduce la volatilidad manteniendo parte de la ventaja matemática.',
    pros: ['Más seguro que Kelly completo', 'Mantiene ventaja matemática', 'Menor volatilidad', 'Recomendado por profesionales'],
    cons: ['Crecimiento algo más lento que Kelly puro', 'Sigue requiriendo buenas estimaciones'],
    example: 'Si Kelly dice 8% → apuestas el 4% (Kelly al 50%)',
    icon: 'ri-percent-line',
  },
  {
    id: 'fibonacci',
    name: 'Sistema Fibonacci',
    risk: 'Alto',
    riskColor: '#ef4444',
    description: 'Aumentas la apuesta siguiendo la secuencia de Fibonacci tras cada pérdida y retrocedes dos pasos tras cada ganancia. Solo recomendado para apuestas cercanas a cuota 2.618.',
    pros: ['Recuperación gradual de pérdidas', 'Menos agresivo que Martingala'],
    cons: ['Requiere bankroll muy grande', 'Rachas negativas largas son devastadoras', 'No recomendado para principiantes'],
    example: '1 → 1 → 2 → 3 → 5 → 8 → 13 → 21... (unidades)',
    icon: 'ri-git-branch-line',
  },
];

export default function BankrollSection() {
  const [active, setActive] = useState('flat');
  const selected = methods.find((m) => m.id === active)!;

  return (
    <section id="bankroll" className="py-20 bg-[#080a0f]">
      <div className="max-w-[1440px] mx-auto px-6">
        {/* Header */}
        <div className="flex items-start gap-4 mb-12">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#00e87a]/10 border border-[#00e87a]/20 flex-shrink-0">
            <i className="ri-funds-line text-[#00e87a] text-xl"></i>
          </div>
          <div>
            <p className="text-[#00e87a] text-xs font-semibold uppercase tracking-widest mb-1">Capítulo 1</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
              Gestión de Bankroll
            </h2>
            <p className="text-[#a8b2c1] text-base max-w-2xl">
              El bankroll es el capital que destinas exclusivamente a las apuestas. Gestionarlo bien es la diferencia entre un apostador amateur y uno profesional. <strong className="text-white">Nunca apuestes dinero que no puedas permitirte perder.</strong>
            </p>
          </div>
        </div>

        {/* Reglas de oro */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-14">
          {[
            { icon: 'ri-shield-line', title: 'Regla del 1-5%', desc: 'Nunca arriesgues más del 1-5% de tu bankroll total en una sola apuesta. El 2% es el estándar profesional.' },
            { icon: 'ri-lock-line', title: 'Bankroll separado', desc: 'Mantén el dinero de apuestas completamente separado de tus finanzas personales. Nunca mezcles.' },
            { icon: 'ri-line-chart-line', title: 'Registro obligatorio', desc: 'Anota cada apuesta: deporte, cuota, stake, resultado. Sin datos no puedes mejorar ni detectar errores.' },
          ].map((r) => (
            <div key={r.title} className="p-5 bg-[#0d0f14] border border-[#1e2028] rounded-xl">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#00e87a]/10 mb-3">
                <i className={`${r.icon} text-[#00e87a] text-base`}></i>
              </div>
              <h4 className="text-white font-semibold text-sm mb-1.5">{r.title}</h4>
              <p className="text-[#6b7280] text-sm leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>

        {/* Métodos de staking */}
        <h3 className="text-white font-bold text-xl mb-6" style={{ fontFamily: "'Syne', sans-serif" }}>Métodos de Staking</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Selector */}
          <div className="flex flex-col gap-2">
            {methods.map((m) => (
              <button
                key={m.id}
                onClick={() => setActive(m.id)}
                className={`flex items-center justify-between px-4 py-3.5 rounded-xl border text-left transition-all cursor-pointer ${
                  active === m.id
                    ? 'bg-[#0d0f14] border-[#00e87a]/40'
                    : 'bg-[#0d0f14] border-[#1e2028] hover:border-[#2a2d3a]'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5">
                    <i className={`${m.icon} text-sm`} style={{ color: active === m.id ? '#00e87a' : '#6b7280' }}></i>
                  </div>
                  <span className={`text-sm font-medium ${active === m.id ? 'text-white' : 'text-[#a8b2c1]'}`}>{m.name}</span>
                </div>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: m.riskColor, backgroundColor: `${m.riskColor}15` }}>
                  {m.risk}
                </span>
              </button>
            ))}
          </div>

          {/* Detail */}
          <div className="lg:col-span-2 bg-[#0d0f14] border border-[#1e2028] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-bold text-lg">{selected.name}</h4>
              <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ color: selected.riskColor, backgroundColor: `${selected.riskColor}15` }}>
                Riesgo: {selected.risk}
              </span>
            </div>
            <p className="text-[#a8b2c1] text-sm leading-relaxed mb-5">{selected.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              <div>
                <p className="text-[#00e87a] text-xs font-semibold uppercase tracking-wider mb-2">Ventajas</p>
                <ul className="space-y-1.5">
                  {selected.pros.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-[#a8b2c1]">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-[#00e87a] text-xs"></i>
                      </div>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-[#ef4444] text-xs font-semibold uppercase tracking-wider mb-2">Desventajas</p>
                <ul className="space-y-1.5">
                  {selected.cons.map((c) => (
                    <li key={c} className="flex items-start gap-2 text-sm text-[#a8b2c1]">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-close-line text-[#ef4444] text-xs"></i>
                      </div>
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="p-3.5 bg-[#080a0f] border border-[#2a2d3a] rounded-lg">
              <p className="text-[#6b7280] text-xs font-semibold uppercase tracking-wider mb-1">Ejemplo práctico</p>
              <p className="text-[#a8b2c1] text-sm font-mono">{selected.example}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
